import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  readTelephoneSubscribers,
  type MedcomTelephoneCode,
} from '@/lib/patientStamkortFields';
import { formatRevealedCpr, MASKED_CPR } from '@/lib/cprDisplay';
import type { PatientRow } from '@/hooks/usePatients';
import { Button } from '@/components/ui/Button';
import { PatientPortalSection } from '@/components/patient/PatientPortalSection';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';
import type { Json } from '@/types/database.generated';

interface AddressShape {
  street: string;
  zip_code: string;
  city: string;
  country: string;
}

function readAddress(value: Json | null): AddressShape {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return { street: '', zip_code: '', city: '', country: 'Danmark' };
  }
  const v = value as Record<string, Json>;
  return {
    street: typeof v.street === 'string' ? v.street : '',
    zip_code: typeof v.zip_code === 'string' ? v.zip_code : typeof v.zip === 'string' ? v.zip : '',
    city: typeof v.city === 'string' ? v.city : '',
    country: typeof v.country === 'string' ? v.country : 'Danmark',
  };
}

function addressLines(address: AddressShape): string[] {
  const street = address.street.trim();
  const cityLine = [address.zip_code, address.city].filter(Boolean).join(' ').trim();
  const country = address.country.trim();
  const omitCountry = country === '' || country === 'Danmark' || country === 'Denmark';
  return [street, cityLine, omitCountry ? '' : country].filter(Boolean);
}

function telephoneTypeLabel(
  code: MedcomTelephoneCode,
  label: (key: 'fastnet' | 'mobil' | 'arbejde') => string,
): string {
  switch (code) {
    case 'fastnet':
      return label('fastnet');
    case 'mobil':
      return label('mobil');
    case 'arbejde':
      return label('arbejde');
    default: {
      const unknown: never = code;
      return unknown;
    }
  }
}

function telHref(value: string): string {
  const trimmed = value.trim();
  if (trimmed.startsWith('+')) return `tel:${trimmed.replace(/[^\d+]/g, '')}`;
  return `tel:${trimmed.replace(/[^\d]/g, '')}`;
}

export const PATIENT_INFO_PANEL_ID = 'patient-oplysninger';

interface PatientOverviewPanelProps {
  patient: PatientRow & { has_cpr: boolean };
  canEdit: boolean;
  canRevealCpr: boolean;
  cprAutoReveal?: boolean;
  revealedCpr: string | null;
  onRevealCpr: () => void;
  cprLoading: boolean;
}

export function PatientOverviewPanel({
  patient,
  canEdit,
  canRevealCpr,
  cprAutoReveal = false,
  revealedCpr,
  onRevealCpr,
  cprLoading,
}: PatientOverviewPanelProps) {
  const { t } = useTranslation();
  const [notesExpanded, setNotesExpanded] = useState(false);
  const addr = readAddress(patient.address);
  const address = addressLines(addr);
  const localeIsEn = (patient.preferred_locale as string | undefined) === 'en-DK';
  const localeLabel = localeIsEn ? t('patients.locale_en') : t('patients.locale_da');
  const primaryTelephone = readTelephoneSubscribers(patient.telephone_subscribers)[0] ?? null;
  const telephoneLabel = primaryTelephone
    ? `${primaryTelephone.identifier} ${telephoneTypeLabel(primaryTelephone.code, (key) =>
        t(`patients.telephone_types.${key}`),
      )}`
    : '—';
  const notes = patient.notes?.trim() ?? '';
  const notesLong = notes.length > 40;
  const yes = t('patients.header.consent_yes');
  const no = t('patients.header.consent_no');
  const cprHiddenLabel = t('patients.cpr_modal.hidden_label');
  const cprValue = patient.has_cpr
    ? revealedCpr
      ? formatRevealedCpr(revealedCpr)
      : MASKED_CPR
    : t('patients.cpr_currently_unset');
  const revealAction =
    patient.has_cpr && canRevealCpr && !cprAutoReveal ? (
      <Button
        type="button"
        variant="link"
        className="h-auto min-h-10 px-0"
        onClick={onRevealCpr}
        disabled={cprLoading}
      >
        {revealedCpr ? t('patients.cpr_hide') : t('patients.cpr_reveal')}
      </Button>
    ) : null;

  return (
    <dl
      id={PATIENT_INFO_PANEL_ID}
      className="mt-s4 grid grid-cols-1 gap-x-s5 gap-y-0 rounded-pop border border-ds-line bg-ds-sunk p-[14px] nav:grid-cols-5 nav:gap-y-s4 nav:p-5"
    >
      <InfoCell
        label={t('patients.field_email')}
        value={patient.email?.trim() || '—'}
        href={patient.email?.trim() ? `mailto:${patient.email.trim()}` : undefined}
      />
      <InfoCell
        label={t('patients.field_phone')}
        value={telephoneLabel}
        href={primaryTelephone ? telHref(primaryTelephone.identifier) : undefined}
      />
      <InfoCell
        label={t('patients.section_cpr')}
        value={cprValue}
        tabular={patient.has_cpr}
        valueAriaLabel={patient.has_cpr && !revealedCpr ? cprHiddenLabel : undefined}
        action={revealAction}
      />
      <InfoCell
        label={t('patients.header.field_job_title')}
        value={patient.job_title?.trim() || '—'}
      />
      <InfoCell label={t('patients.header.field_language')} value={localeLabel} />
      <InfoCell
        label={t('patients.section_address')}
        value={address.length > 0 ? address.join('\n') : '—'}
        multiline
      />
      <InfoCell
        className="nav:col-span-2"
        label={t('patients.section_notes')}
        value={notes || '—'}
        clamp={!notesExpanded && notes.length > 0}
        action={
          notesLong ? (
            <Button
              type="button"
              variant="link"
              className="h-auto min-h-10 px-0"
              onClick={() => setNotesExpanded((open) => !open)}
              aria-expanded={notesExpanded}
            >
              {notesExpanded ? t('patients.header.show_less') : t('patients.header.show_more')}
            </Button>
          ) : undefined
        }
      />
      <InfoCell
        label={t('patients.header.field_marketing')}
        value={t('patients.header.marketing_channels', {
          email: patient.marketing_consent_email ? yes : no,
          sms: patient.marketing_consent_sms ? yes : no,
        })}
      />
      <InfoCell
        label={t('patients.portal.title')}
        value={<PatientPortalSection patient={patient} canManage={canEdit} variant="details" />}
      />
    </dl>
  );
}

function InfoCell({
  label,
  value,
  href,
  tabular,
  multiline,
  clamp,
  valueAriaLabel,
  action,
  className,
}: {
  label: string;
  value: ReactNode;
  href?: string;
  tabular?: boolean;
  multiline?: boolean;
  clamp?: boolean;
  valueAriaLabel?: string;
  action?: ReactNode;
  className?: string;
}) {
  const valueClass = cn(
    TYPE.control,
    'min-w-0 text-ds-text [overflow-wrap:anywhere]',
    tabular && 'tracking-[0.06em] tabular-nums',
    multiline && 'whitespace-pre-line',
    clamp && 'line-clamp-2 nav:line-clamp-1',
  );
  const body =
    href && typeof value === 'string' && value !== '—' ? (
      <a href={href} className={valueClass}>
        {value}
      </a>
    ) : typeof value === 'string' ? (
      <span className={valueClass} aria-label={valueAriaLabel}>
        {value}
      </span>
    ) : (
      value
    );

  return (
    <div
      className={cn(
        'flex items-start justify-between gap-s3 border-b border-ds-line py-s3 last:border-b-0',
        'nav:block nav:border-b-0 nav:py-0',
        className,
      )}
    >
      <dt className={cn(TYPE.meta, 'w-[104px] shrink-0 text-ds-muted nav:w-auto')}>{label}</dt>
      <dd className="flex min-w-0 flex-1 flex-col items-end gap-s1 text-right nav:mt-s1 nav:items-start nav:text-left">
        <span className="inline-flex min-w-0 max-w-full flex-wrap items-center justify-end gap-s2 nav:justify-start">
          {body}
          {action}
        </span>
      </dd>
    </div>
  );
}
